import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation } from '@tanstack/react-query';
import { App, AutoComplete, Button, Checkbox, Col, Form, Input, InputNumber, Row, Select, Tabs } from 'antd';
import { useForm } from 'antd/es/form/Form';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField, updateProps } from '../../../contextRedux/DeviceType/DeviceTypeModel';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import { RootState } from '../../../store';
import useWindowSize from '../../../utils/useWindowSize';

const { Option } = Select;

const DeviceForm: React.FC<any> = () => {
  const [form] = useForm();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();
  const dispatch = useDispatch();
  const width = useWindowSize();

  const systemTypes = useSelector((state: RootState) => state.store.systemType.data || []);
  const deviceType = useSelector((state: RootState) => state.models.deviceType);

  const initialValues = {
    id: useSelector((state: RootState) => state?.models?.deviceType?.id) ?? '',
    systemTypeId: useSelector((state: RootState) => state?.models?.deviceType?.systemTypeId) ?? '',
    name: useSelector((state: RootState) => state?.models?.deviceType?.name) ?? '',
    price: useSelector((state: RootState) => state?.models?.deviceType?.price) ?? '',
    addition: useSelector((state: RootState) => state?.models?.deviceType?.addition) ?? '',
    producer: useSelector((state: RootState) => state?.models?.deviceType?.producer) ?? '',
    provider: useSelector((state: RootState) => state?.models?.deviceType?.provider) ?? '',
    model: useSelector((state: RootState) => state?.models?.deviceType?.model) ?? '',
    laborTimeSM: useSelector((state: RootState) => state?.models?.deviceType?.laborTimeSM) ?? '',
    laborPriceSM: useSelector((state: RootState) => state?.models?.deviceType?.laborPriceSM) ?? '',
    laborTimePM: useSelector((state: RootState) => state?.models?.deviceType?.laborTimePM) ?? '',
    laborPricePM: useSelector((state: RootState) => state?.models?.deviceType?.laborPricePM) ?? '',
    minRecomandation: useSelector((state: RootState) => state?.models?.deviceType?.minRecomandation) ?? '',
    maxRecomandation: useSelector((state: RootState) => state?.models?.deviceType?.maxRecomandation) ?? '',
    imageLink: useSelector((state: RootState) => state?.models?.deviceType?.imageLink) ?? '',
    deviceTypeLabors: useSelector((state: RootState) => state?.models?.deviceType?.deviceTypeLabors) ?? null,
    isActive: useSelector((state: RootState) => state?.models?.deviceType?.isActive) ?? '',
  };

  const handleSelectSystem = (value: string) => {
    const systemType = systemTypes?.find((sc) => sc.name === value);
    const fieldsValue = {
      ...form.getFieldsValue(true),
      systemTypeId: systemType?.id,
      systemType: systemType,
    };

    form.setFieldsValue(fieldsValue);
    dispatch(updateField({ field: 'systemTypeId', value: systemType.id }));
    dispatch(updateField({ field: 'systemType', value: systemType }));
  };

  const onSaveDeviceType = async () => {
    saveDeviceType(deviceType);
  };

  const { mutate: saveDeviceType, isPending: _isSavingDevice } = useMutation({
    mutationFn: (formData: any) => DeviceTypeService.upsert(deviceType.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        dispatch(updateProps({ field: 'isModalVisible', value: false }));
        dispatch(updateProps({ field: 'refresh', value: !deviceType.props.refresh }));
        dispatch(updateProps({ field: 'isFinish', value: true }));
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldsValue = {
      ...form.getFieldsValue(true),
      isActive: e.target.checked,
    };
    form.setFieldsValue(fieldsValue);
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const handleFieldChange = (changedValues: any, _allValues: any) => {
    for (const key in changedValues) {
      dispatch(updateField({ field: key, value: changedValues[key] }));
    }
  };

  useEffect(() => {
    form.setFieldsValue(deviceType);
  }, [deviceType]);

  const tabs = [
    {
      key: 'baseTab',
      label: 'Device',
      children: (
        <Row gutter={[16, 16]} className='row'>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item label='Denumire' name='name' rules={[{ required: true, message: 'Introdu denumire!' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Tip de sistem'
              name={['systemType', 'name']}
              rules={[{ required: true, message: 'Introdu tipul de sistem!' }]}
            >
              <AutoComplete onSelect={handleSelectSystem} filterOption={handleFilter}>
                {systemTypes?.map((item) => (
                  <Option key={item.id} value={item.name}>
                    {item.name}
                  </Option>
                ))}
              </AutoComplete>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Producator'
              name='producer'
              rules={[{ required: true, message: 'Introdu producatorul!' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item label='Furnizor' name='provider' rules={[{ required: true, message: 'Introdu furnizorul!' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item label='Model' name='model' rules={[{ required: true, message: 'Introdu model!' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item label='Pret' name='price' rules={[{ required: true, message: 'Introdu pretul!' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Adaos'
              name='addition'
              rules={[{ required: true, message: 'Introdu adaos!' }]}
              initialValue={50}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Link imagine'
              name='imageLink'
              rules={[{ required: true, message: 'Introdu linkul imaginii!' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Timp Manopera SM (Minute)'
              name='laborTimeSM'
              rules={[{ required: true, message: 'Introdu timp manopera!' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Pret Manopera SM'
              name='laborPriceSM'
              rules={[{ required: true, message: 'Introdu pret manopera!' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Timp Manopera PM (Minute)'
              name='laborTimePM'
              rules={[{ required: true, message: 'Introdu timp manopera!' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Pret Manopera PM'
              name='laborPricePM'
              rules={[{ required: true, message: 'Introdu pret manopera!' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Valoare Minima Recomandata'
              name='minRecomandation'
              rules={[{ required: true, message: 'Introdu minimul recomandat!' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item
              label='Valoarea Maxima Recomandata'
              name='maxRecomandation'
              rules={[{ required: true, message: 'Introdu maximul recomandat!' }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
            <Form.Item label='Status' name='isActive' valuePropName='checked'>
              <Checkbox onChange={() => handleCheckboxChange} />
            </Form.Item>
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <Form
      form={form}
      name='deviceTypeForm'
      initialValues={initialValues}
      onValuesChange={handleFieldChange}
      onFinish={onSaveDeviceType}
      layout='vertical'
    >
      <Tabs defaultActiveKey='baseTab'>
        {tabs?.map((tab) => (
          <Tabs.TabPane key={tab.key} tab={tab.label}>
            {tab.children}
          </Tabs.TabPane>
        ))}
      </Tabs>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Finalizare
        </Button>
      </Form.Item>
    </Form>
  );
};

export default DeviceForm;
